import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbCollapseModule, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ViewType } from '@ortho-next/nextray-core';
import Slider from 'bootstrap-slider';
import { BridgeMessages } from '../../../nextray/Core/Bridge';
import { BaseComponent, ConfirmationComponent } from '../../shared';


/**
* Component of image elaboration tools.
*/
@Component({
	selector: 'image-elaboration',
	templateUrl: './image-elaboration.component.html',
	imports: [
		NgbCollapseModule
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ImageElaborationComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly modalSrv = inject(NgbModal);

	@Input() set view(val: ViewType | undefined) {
		this.initBrightness();
		this.initContrast();
	}

	isCollapsed = true;

	brightnessSlider: any;
	contrastSlider: any;


	ngOnInit(): void {
		this.initSliders();
		this.canvasSrv.addEventListener('onResult', this.historyUpdate);
	}

	/**
	* Event listener for history update.
	*/
	historyUpdate = (event: { args: keyof BridgeMessages }) => {
		if (event.args == 'historyChanged') {
			this.initBrightness();
			this.initContrast();
		}
	};

	private initSliders(): void {
		const sliderOptions = { id: "slider12a", min: -1, max: 1, step: 0.1, value: 0, tooltip: 'hide' };
		this.brightnessSlider = new Slider("#brightnessSlider", sliderOptions)
			.on('change', (val: any) => this.setBrightness(val))
			.on('slideStart', () => this.canvasSrv.dispatch('startTrackingPlane'))
			.on('slideStop', () => this.canvasSrv.dispatch('detectChangesPlane'));
		this.contrastSlider = new Slider("#contrastSlider", sliderOptions)
			.on('change', (val: any) => this.setContrast(val))
			.on('slideStart', () => this.canvasSrv.dispatch('startTrackingPlane'))
			.on('slideStop', () => this.canvasSrv.dispatch('detectChangesPlane'));
		this.initBrightness();
		this.initContrast();
	}

	private initBrightness(): void {
		if (this.brightnessSlider) {
			this.activeView === this.activeViewEnum.AP ? this.brightnessSlider.setValue(this.brightnessAP) : this.brightnessSlider.setValue(this.brightnessLT);
		}
	}

	private initContrast(): void {
		if (this.contrastSlider) {
			this.activeView === this.activeViewEnum.AP ? this.contrastSlider.setValue(this.contrastAP) : this.contrastSlider.setValue(this.contrastLT);
		}
	}

	private setBrightness(result: { oldValue: number, newValue: number }): void {
		this.canvasSrv.dispatch('setBrightness', result.newValue);
	}

	private setContrast(result: { oldValue: number, newValue: number }): void {
		this.canvasSrv.dispatch('setContrast', result.newValue);
	}

	/**
	* Add line tool on active view.
	*/
	addLine(): void {
		this.canvasSrv.dispatch('addLine');
	}

	/**
	* Add goniometer tool on active view.
	*/
	addGoniometer(): void {
		this.canvasSrv.dispatch('addGoniometer');
	}

	/**
	* Indicate if measurement tool delete button is enabled in active view.
	*/
	get isDeleteEnabled() {
		return this.activeView === ViewType.AP ? this.isMeasurementToolSelectedAP : this.isMeasurementToolSelectedLT;
	}

	/**
	* Delete selected measurement tool on active view.
	*/
	deleteTool(): void {
		if (this.isDeleteEnabled) {
			this.canvasSrv.dispatch('deleteMeasurementTool');
		}
	}

	/**
	* Indicate if editing tool button is enabled.
	*/
	get isEditToolEnabled(): boolean {
		return !this.stateType || this.stateType !== this.stateTypeEnum.RPM;
	}

	/**
	* Open rotation confirmation modal.
	*/
	openRotateModal(): void {
		if (this.isEditToolEnabled) {
			const message: string = this.labels().IMAGE_ELABORATION_COMPONENT_WARNING_MODAL_MESSAGE;
			this.openWarningModal(message, this.startRotateWorkflow);
		}
	}

	/**
	* Start rotation workflow.
	*/
	private startRotateWorkflow(): void {
		if (this.stateType !== this.stateTypeEnum.calibrated) {
			this.canvasSrv.dispatch('switchToCalibrated');
			this.canvasSrv.saveState();
		}
		this.canvasSrv.dispatch('startRotate');
	}

	/**
	* Open cropping confirmation modal.
	*/
	openCropModal(): void {
		if (this.isEditToolEnabled) {
			const message: string = this.labels().IMAGE_ELABORATION_COMPONENT_WARNING_MODAL_MESSAGE;
			this.openWarningModal(message, this.startCropWorkflow);
		}
	}

	/**
	* Start cropping workflow.
	*/
	private startCropWorkflow(): void {
		if (this.stateType !== this.stateTypeEnum.calibrated) {
			this.canvasSrv.dispatch('switchToCalibrated');
			this.canvasSrv.saveState();
		}
		this.canvasSrv.dispatch('startCrop');
	}

	private openWarningModal(message: string, confirmFunction: Function, cancelFunction?: Function): void {
		if (!this.modalSrv.hasOpenModals()) {
			const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().IMAGE_ELABORATION_COMPONENT_WARNING_MODAL_TITLE,
				message: message
			};
			modalRef.result.then(() => confirmFunction.call(this), () => cancelFunction ? cancelFunction.call(this) : null).catch(error => false);
		}
	}


	ngOnDestroy(): void {
		this.canvasSrv.removeEventListener('onResult', this.historyUpdate);
	}

}
